import React, { useState, useRef, useEffect } from 'react';
import './ChatInterface.css';

// API基础URL配置
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://ai-learning-backend.risingstarintl.biz';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: Array<{
    document_id?: string;
    filename?: string;
    content: string;
  }>;
}

interface ChatSession {
  id: string;
  knowledge_base_id: string;
  user_id?: string;
  session_name: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface ChatInterfaceProps {
  knowledgeBaseId: string;
  knowledgeBaseName: string;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ knowledgeBaseId, knowledgeBaseName }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [showSessions, setShowSessions] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 自动滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // 加载聊天会话列表
  const loadChatSessions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/autorag/sessions/${knowledgeBaseId}`);
      const result = await response.json();

      if (result.success && result.data) {
        setSessions(result.data);
        // 如果没有当前会话，选择最新的一个
        if (!currentSessionId && result.data.length > 0) {
          setCurrentSessionId(result.data[0].id);
        }
      }
    } catch (error) {
      console.error('加载聊天会话失败:', error);
    }
  };

  // 创建新的聊天会话
  const createNewSession = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/autorag/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          knowledgeBaseId,
          sessionName: `新对话 ${new Date().toLocaleString()}`
        })
      });

      const result = await response.json();
      if (result.success) {
        setCurrentSessionId(result.data.id);
        setMessages([]);
        await loadChatSessions();
      }
    } catch (error) {
      console.error('创建新会话失败:', error);
    }
  };

  // 切换会话
  const switchSession = async (sessionId: string) => {
    setCurrentSessionId(sessionId);
    setMessages([]);
    setCurrentPage(1);
    setHasMoreHistory(true);
    await loadChatHistory(sessionId, true);
    setShowSessions(false);
  };

  // 加载聊天历史
  const loadChatHistory = async (sessionId: string, reset = false) => {
    if (loadingHistory) return;

    setLoadingHistory(true);
    try {
      const page = reset ? 1 : currentPage;
      const response = await fetch(
        `${API_BASE_URL}/api/autorag/history/${knowledgeBaseId}?sessionId=${sessionId}&page=${page}&limit=20`
      );
      const result = await response.json();

      if (result.success && result.data) {
        const historyMessages: Message[] = result.data.map((item: any) => [
          {
            id: item.id,
            role: 'user' as const,
            content: item.question,
            timestamp: new Date(item.created_at)
          },
          {
            id: `${item.id}_answer`,
            role: 'assistant' as const,
            content: item.answer,
            timestamp: new Date(item.created_at),
            sources: item.sources || []
          }
        ]).flat();

        if (reset) {
          // 重置时，API返回DESC排序（最新的在前），需要反转顺序
          // 但是要保持每个问答对的正确顺序
          const reversedMessages: Message[] = [];
          for (let i = historyMessages.length - 1; i >= 0; i -= 2) {
            if (i > 0) {
              // 添加问答对：用户问题在前，AI回答在后
              reversedMessages.push(historyMessages[i - 1]); // 用户消息
              reversedMessages.push(historyMessages[i]);     // AI回答
            } else {
              // 如果只有一个消息（奇数情况）
              reversedMessages.push(historyMessages[i]);
            }
          }
          setMessages(reversedMessages);
          setCurrentPage(1);
        } else {
          // 加载更多时，将新消息添加到前面
          const reversedMessages: Message[] = [];
          for (let i = historyMessages.length - 1; i >= 0; i -= 2) {
            if (i > 0) {
              reversedMessages.push(historyMessages[i - 1]);
              reversedMessages.push(historyMessages[i]);
            } else {
              reversedMessages.push(historyMessages[i]);
            }
          }
          setMessages(prev => [...reversedMessages, ...prev]);
          setCurrentPage(prev => prev + 1);
        }

        // 检查是否还有更多历史
        if (result.pagination) {
          setHasMoreHistory(result.pagination.page < result.pagination.totalPages);
        }
      }
    } catch (error) {
      console.error('加载聊天历史失败:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // 加载更多历史
  const loadMoreHistory = () => {
    if (currentSessionId && hasMoreHistory && !loadingHistory) {
      loadChatHistory(currentSessionId, false);
    }
  };

  // 初始加载
  useEffect(() => {
    loadChatSessions();
  }, [knowledgeBaseId]);

  // 当会话改变时加载历史
  useEffect(() => {
    if (currentSessionId) {
      loadChatHistory(currentSessionId, true);
    }
  }, [currentSessionId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 发送消息
  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/autorag/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          knowledgeBaseId,
          question: inputValue,
          sessionId: currentSessionId,
          history: messages.map(msg => ({
            role: msg.role,
            content: msg.content
          }))
        })
      });

      const result = await response.json();

      if (result.success) {
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: result.data.answer,
          timestamp: new Date(),
          sources: result.data.sources
        };

        setMessages(prev => [...prev, assistantMessage]);

        // 更新会话ID（如果是新会话）
        if (result.data.session_id && !currentSessionId) {
          setCurrentSessionId(result.data.session_id);
          await loadChatSessions();
        }
      } else {
        // 显示错误消息
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: `抱歉，我遇到了一些问题：${result.error}`,
          timestamp: new Date()
        };

        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('发送消息失败:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: '抱歉，网络连接出现问题，请稍后再试。',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // 处理回车键
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // 渲染消息内容
  const renderMessage = (message: Message) => {
    return (
      <div className={`message ${message.role}`}>
        <div className="message-content">
          <div className="message-text">{message.content}</div>

          {/* 显示文档来源 */}
          {message.sources && message.sources.length > 0 && (
            <div className="message-sources">
              <div className="sources-header">
                📚 参考文档 ({message.sources.length}个)
              </div>
              <div className="sources-list">
                {message.sources.map((source, index) => (
                  <div key={index} className="source-item">
                    <div className="source-filename">
                      📄 {source.filename || '未知文档'}
                    </div>
                    <div className="source-content">
                      {source.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="message-time">
          {message.timestamp.toLocaleTimeString('zh-CN')}
        </div>
      </div>
    );
  };

  return (
    <div className="chat-interface">
      {/* 聊天头部 */}
      <div className="chat-header">
        <div className="header-left">
          <h3>🤖 智能问答 - {knowledgeBaseName}</h3>
          <p>基于知识库内容进行智能问答</p>
        </div>
        <div className="header-right">
          <button
            className="session-button"
            onClick={() => setShowSessions(!showSessions)}
          >
            💬 会话管理
          </button>
          <button
            className="new-session-button"
            onClick={createNewSession}
          >
            ➕ 新对话
          </button>
        </div>
      </div>

      {/* 会话列表 */}
      {showSessions && (
        <div className="sessions-panel">
          <div className="sessions-header">
            <h4>对话会话</h4>
            <button onClick={() => setShowSessions(false)}>✕</button>
          </div>
          <div className="sessions-list">
            {sessions.length === 0 ? (
              <div className="no-sessions">
                <p>暂无对话会话</p>
                <button onClick={createNewSession}>创建第一个对话</button>
              </div>
            ) : (
              sessions.map(session => (
                <div
                  key={session.id}
                  className={`session-item ${currentSessionId === session.id ? 'active' : ''}`}
                  onClick={() => switchSession(session.id)}
                >
                  <div className="session-name">{session.session_name}</div>
                  <div className="session-time">
                    {new Date(session.updated_at).toLocaleString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* 消息列表 */}
      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">💬</div>
            <h3>开始对话</h3>
            <p>向AI助手提问关于知识库内容的问题</p>
          </div>
        ) : (
          <>
            {messages.map((message) => renderMessage(message))}

            {/* 加载更多历史按钮 - 移到消息列表后面 */}
            {hasMoreHistory && (
              <div className="load-more-container">
                <button
                  className="load-more-button"
                  onClick={loadMoreHistory}
                  disabled={loadingHistory}
                >
                  {loadingHistory ? '⏳ 加载中...' : '📚 加载更多历史'}
                </button>
              </div>
            )}
          </>
        )}

        {isLoading && (
          <div className="message assistant">
            <div className="message-content">
              <div className="loading-indicator">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <div className="loading-text">AI正在思考中...</div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* 输入区域 */}
      <div className="input-container">
        <div className="input-wrapper">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="输入您的问题..."
            disabled={isLoading}
            rows={1}
            className="message-input"
          />
          <button
            onClick={sendMessage}
            disabled={!inputValue.trim() || isLoading}
            className="send-button"
          >
            {isLoading ? '⏳' : '📤'}
          </button>
        </div>
        <div className="input-tips">
          💡 提示：您可以询问关于知识库内容的具体问题，AI会基于文档内容为您解答
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
